import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Mail, CheckCircle, XCircle, ArrowLeft, Bell, BellOff } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function UnsubscribePage() {
  const [searchParams] = useSearchParams();
  const email = searchParams.get('email');
  const token = searchParams.get('token');
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);
  const [procesando, setProcesando] = useState(false);
  const [resultado, setResultado] = useState(null);

  useEffect(() => {
    verificarLink();
  }, []);

  const verificarLink = async () => {
    if (!email || !token) {
      setError('Link inválido. Faltan parámetros.');
      setLoading(false);
      return;
    }

    try {
      const res = await axios.get(`${API}/api/email-templates/unsubscribe/verificar`, {
        params: { email, token }
      });
      setInfo(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Link inválido o expirado');
    } finally {
      setLoading(false);
    }
  };

  const handleUnsubscribe = async () => {
    setProcesando(true);
    try {
      const res = await axios.post(`${API}/api/email-templates/unsubscribe/confirmar`, {
        email,
        token
      });
      setResultado({ success: true, mensaje: res.data.mensaje });
      setInfo(prev => ({ ...prev, ya_dado_de_baja: true }));
    } catch (err) {
      setResultado({ success: false, mensaje: err.response?.data?.detail || 'Error al procesar' });
    } finally {
      setProcesando(false);
    }
  };

  const handleResubscribe = async () => {
    setProcesando(true);
    try {
      const res = await axios.post(`${API}/api/email-templates/unsubscribe/resubscribe`, {
        email,
        token
      });
      setResultado({ success: true, mensaje: res.data.mensaje });
      setInfo(prev => ({ ...prev, ya_dado_de_baja: false }));
    } catch (err) {
      setResultado({ success: false, mensaje: err.response?.data?.detail || 'Error al procesar' });
    } finally {
      setProcesando(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-violet-600 to-violet-700 px-6 py-8 text-center">
            <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Mail className="h-8 w-8 text-white" />
            </div>
            <h1 className="text-xl font-bold text-white">Recordatorios de Vencimiento</h1>
            <p className="text-violet-200 text-sm mt-1">CATSLZ - Colegio de Trabajadorxs Sociales</p>
          </div>

          {/* Content */}
          <div className="p-6">
            {error ? (
              <div className="text-center">
                <XCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
                <h2 className="text-lg font-semibold text-gray-900 mb-2">Link Inválido</h2>
                <p className="text-gray-600 mb-6">{error}</p>
                <Link to="/">
                  <Button variant="outline">
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Volver al inicio
                  </Button>
                </Link>
              </div>
            ) : resultado ? (
              <div className="text-center">
                {resultado.success ? (
                  <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
                ) : (
                  <XCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
                )}
                <h2 className="text-lg font-semibold text-gray-900 mb-2">
                  {resultado.success ? '¡Listo!' : 'Error'}
                </h2>
                <p className="text-gray-600 mb-6">{resultado.mensaje}</p>
                
                {info?.ya_dado_de_baja ? (
                  <Button variant="outline" onClick={handleResubscribe} disabled={procesando}>
                    <Bell className="h-4 w-4 mr-2" />
                    Volver a recibir recordatorios
                  </Button>
                ) : (
                  <Button variant="outline" onClick={handleUnsubscribe} disabled={procesando}>
                    <BellOff className="h-4 w-4 mr-2" />
                    No recibir recordatorios
                  </Button>
                )}
              </div>
            ) : info?.ya_dado_de_baja ? (
              <div className="text-center">
                <BellOff className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h2 className="text-lg font-semibold text-gray-900 mb-2">Ya estás dado de baja</h2>
                <p className="text-gray-600 mb-2">
                  El email <strong>{info.email}</strong> no recibe recordatorios de vencimiento.
                </p>
                <p className="text-gray-500 text-sm mb-6">
                  Fecha de baja: {info.fecha_baja}
                </p>
                <Button onClick={handleResubscribe} disabled={procesando}>
                  <Bell className="h-4 w-4 mr-2" />
                  {procesando ? 'Procesando...' : 'Volver a recibir recordatorios'}
                </Button>
              </div>
            ) : (
              <div className="text-center">
                <Mail className="h-12 w-12 text-violet-500 mx-auto mb-4" />
                <h2 className="text-lg font-semibold text-gray-900 mb-2">¿Querés darte de baja?</h2>
                <p className="text-gray-600 mb-2">
                  Vas a dejar de recibir <strong>recordatorios de vencimiento de matrícula</strong> en:
                </p>
                <p className="font-medium text-gray-900 mb-6">{info?.email}</p>
                
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 text-left">
                  <p className="text-blue-800 text-sm">
                    <strong>Nota:</strong> Solo dejarás de recibir los recordatorios mensuales de vencimiento de cuota. 
                    Otros emails importantes del Colegio (recuperación de contraseña, capacitaciones, etc.) seguirán llegando normalmente.
                  </p>
                </div>
                
                <div className="flex flex-col gap-3">
                  <Button onClick={handleUnsubscribe} disabled={procesando} variant="destructive">
                    <BellOff className="h-4 w-4 mr-2" />
                    {procesando ? 'Procesando...' : 'Confirmar: No recibir recordatorios'}
                  </Button>
                  <Link to="/">
                    <Button variant="outline" className="w-full">
                      Cancelar
                    </Button>
                  </Link>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-6 py-4 text-center border-t">
            <p className="text-xs text-gray-500">
              Colegio de Trabajadorxs Sociales - Distrito Lomas de Zamora
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
